<template>
    <div class="p-4 sm:p-6">
      <!-- Header -->
      <div class="mb-6 sm:mb-8">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Customer Management</h1>
      </div>

      <!-- Filters -->
      <CustomerFilters
        v-model:search="searchQuery"
        v-model:status="statusFilter"
        v-model:brand="brandFilter"
        @apply="applyFilters"
        @reset="resetFilters"
      />

      <!-- Data Display: Desktop Table / Mobile Cards -->
      <div class="bg-white dark:bg-black rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">

        <!-- Loading State -->
        <div v-if="loading">
          <!-- Desktop Table Skeleton -->
          <SkeletonLoader type="table" :rows="5" :columns="6" />

          <!-- Mobile Card Skeleton -->
          <SkeletonLoader type="card" :rows="5" />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-16 px-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-16 w-16 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Error loading customers</h3>
          <p class="text-red-500 dark:text-red-400 mb-6 max-w-sm mx-auto">{{ error }}</p>
          <button
            class="bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm"
            @click="customerStore.fetchCustomers()"
          >
            Try Again
          </button>
        </div>

        <!-- Customer Table -->
        <div v-else-if="customers.length > 0">
          <CustomerTable
            :customers="customers"
            :loading="loading"
            @view="viewCustomer"
            @edit="editCustomer"
            @reset-password="openResetPasswordModal"
            @delete="openDeleteModal"
            @update-category="openCategoryModal"
          />
        </div>

        <!-- Empty State -->
        <div
          v-else
          class="text-center py-16 px-6"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            {{ searchQuery || statusFilter ? 'No customers match your search criteria.' : 'No customers found.' }}
          </p>
        </div>

        <!-- Pagination -->
        <div v-if="customers.length > 0">
          <CustomerPagination
            :current-page="pagination.current_page"
            :last-page="pagination.last_page"
            :per-page="pagination.per_page"
            :total="pagination.total"
            @prev="goToPreviousPage"
            @next="goToNextPage"
            @goto="goToPage"
          />
        </div>

      </div>
    </div>

  <!-- Customer Details Modal -->
  <CustomerDetailsModal
    :is-open="showDetailsModal"
    :customer="selectedCustomer"
    @close="closeDetailsModal"
    @edit="editCustomer"
    @refresh="handleCustomerRefresh"
  />

  <!-- Customer Edit Modal -->
  <CustomerEditModal
    :is-open="showEditModal"
    :customer="selectedCustomer"
    :loading="loading"
    :error="editModalError"
    @close="closeEditModal"
    @submit="handleUpdateCustomer"
  />

  <!-- Delete Confirmation Modal -->
  <DeleteConfirmationModal
    :is-open="showDeleteModal"
    :is-loading="loading"
    :entity-name="customerToDelete ? `${customerToDelete.first_name} ${customerToDelete.last_name}` : ''"
    entity-type="customer"
    @close="closeDeleteModal"
    @confirm="confirmDelete"
  />

  <!-- Reset Password Modal -->
  <ResetPasswordModal
    :is-open="showResetPasswordModal"
    :customer="customerToResetPassword"
    :loading="loading"
    :error="error"
    @close="closeResetPasswordModal"
    @confirm="confirmResetPassword"
  />

  <!-- Customer Category Modal -->
  <CustomerCategoryModal
    :is-open="showCategoryModal"
    :customer="customerToUpdateCategory"
    :loading="loading"
    @close="closeCategoryModal"
    @submit="handleUpdateCategory"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminCustomerStore } from '@/stores/adminCustomerStore';
import { useAuthStore } from '@/stores/auth';
import type { Customer, CustomerUpdateData, CustomerCategoryUpdateData } from '@/types/customer';
import CustomerFilters from '@/components/admin/CustomerFilters.vue';
import CustomerTable from '@/components/admin/CustomerTable.vue';
import CustomerPagination from '@/components/admin/CustomerPagination.vue';
import CustomerDetailsModal from '@/components/admin/CustomerDetailsModal.vue';
import CustomerEditModal from '@/components/admin/CustomerEditModal.vue';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import ResetPasswordModal from '@/components/admin/ResetPasswordModal.vue';
import CustomerCategoryModal from '@/components/admin/CustomerCategoryModal.vue';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';

const toast = useToast();
const customerStore = useAdminCustomerStore();
const authStore = useAuthStore();

// Local state
const searchQuery = ref('');
const statusFilter = ref('');
const brandFilter = ref(String(authStore.adminBrandId ?? ''));
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showResetPasswordModal = ref(false);
const showCategoryModal = ref(false);
const selectedCustomer = ref<Customer | null>(null);
const customerToDelete = ref<Customer | null>(null);
const customerToResetPassword = ref<Customer | null>(null);
const customerToUpdateCategory = ref<Customer | null>(null);
const editModalError = ref<string | null>(null);

// Computed properties from store
const customers = computed(() => customerStore.customers);
const loading = computed(() => customerStore.loading);
const error = computed(() => customerStore.error);
const pagination = computed(() => customerStore.pagination);

// Methods
function applyFilters() {
  customerStore.setFilters({
    search: searchQuery.value,
    status: statusFilter.value,
    brand: brandFilter.value,
    page: 1,
    per_page: 10 // Explicitly set per_page to ensure it's always 10
  });
  customerStore.fetchCustomers();
}

function resetFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  brandFilter.value = String(authStore.adminBrandId ?? '');
  customerStore.resetFilters();
  customerStore.setFilters({ brand: String(authStore.adminBrandId ?? '') });
  customerStore.fetchCustomers();
}

function goToPreviousPage() {
  if (pagination.value.current_page > 1) {
    customerStore.setFilters({ page: pagination.value.current_page - 1 });
    customerStore.fetchCustomers();
  }
}

function goToNextPage() {
  if (pagination.value.current_page < pagination.value.last_page) {
    customerStore.setFilters({ page: pagination.value.current_page + 1 });
    customerStore.fetchCustomers();
  }
}

function goToPage(page: number) {
  customerStore.setFilters({ page });
  customerStore.fetchCustomers();
}

async function viewCustomer(customer: Customer) {
  try {
    // Fetch full customer details with vehicles and tires
    await customerStore.fetchCustomer(customer.user_id);
    selectedCustomer.value = customerStore.selectedCustomer;
    showDetailsModal.value = true;
  } catch (err) {
    toast.error('Failed to load customer details');
  }
}

function editCustomer(customer: Customer) {
  selectedCustomer.value = customer;
  showEditModal.value = true;
  showDetailsModal.value = false;
}

async function handleCustomerRefresh() {
  // Refresh the customer details after tire approval/rejection
  if (selectedCustomer.value) {
    try {
      await customerStore.fetchCustomer(selectedCustomer.value.user_id);
      selectedCustomer.value = customerStore.selectedCustomer;
    } catch (err) {
      toast.error('Failed to refresh customer details');
    }
  }
}

function openResetPasswordModal(customer: Customer) {
  customerToResetPassword.value = customer;
  showResetPasswordModal.value = true;
}

function closeResetPasswordModal() {
  showResetPasswordModal.value = false;
  customerToResetPassword.value = null;
}

async function confirmResetPassword() {
  if (!customerToResetPassword.value) return;

  try {
    await customerStore.resetCustomerPassword(customerToResetPassword.value.user_id);
    toast.success('Password reset link sent successfully to ' + customerToResetPassword.value.email);
    closeResetPasswordModal();
  } catch (err) {
    toast.error('Failed to send password reset link');
  }
}

function openDeleteModal(customer: Customer) {
  customerToDelete.value = customer;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  customerToDelete.value = null;
}

async function confirmDelete() {
  if (!customerToDelete.value) return;

  try {
    await customerStore.deleteCustomer(customerToDelete.value.user_id);
    toast.success('Customer deleted successfully');
    closeDeleteModal();
  } catch (err) {
    toast.error('Failed to delete customer');
  }
}

async function handleUpdateCustomer(data: CustomerUpdateData) {
  if (!selectedCustomer.value) return;

  editModalError.value = null; // Clear previous errors

  try {
    await customerStore.updateCustomer(selectedCustomer.value.user_id, data);
    toast.success('Customer updated successfully');
    closeEditModal();
  } catch (err: any) {
    // Extract user-friendly error message
    let errorMessage = 'Failed to update customer'

    if (err.response?.data?.message) {
      // Use the specific error message from backend
      errorMessage = err.response.data.message
    } else if (err.response?.data?.errors) {
      // If there are field-specific errors, get the first one
      const errors = err.response.data.errors
      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey && errors[firstErrorKey]) {
        errorMessage = Array.isArray(errors[firstErrorKey])
          ? errors[firstErrorKey][0]
          : errors[firstErrorKey]
      }
    }

    // Set error for modal display
    editModalError.value = errorMessage

    // Show error in toast
    toast.error(errorMessage)

    // Don't close modal - let user see the error and fix it
  }
}

function openCategoryModal(customer: Customer) {
  customerToUpdateCategory.value = customer;
  showCategoryModal.value = true;
}

function closeCategoryModal() {
  showCategoryModal.value = false;
  customerToUpdateCategory.value = null;
}

async function handleUpdateCategory(data: CustomerCategoryUpdateData) {
  if (!customerToUpdateCategory.value) return;
  try {
    await customerStore.updateCustomerCategory(customerToUpdateCategory.value.user_id, data);
    toast.success('Customer category updated successfully');
    closeCategoryModal();
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Failed to update category');
  }
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedCustomer.value = null;
}

function closeEditModal() {
  showEditModal.value = false;
  selectedCustomer.value = null;
  editModalError.value = null;
  customerStore.clearError();
}

// Lifecycle
watch(
  () => authStore.adminBrandId,
  (newBrandId) => {
    brandFilter.value = String(newBrandId ?? '');
    customerStore.resetFilters();
    customerStore.setFilters({ brand: String(newBrandId ?? '') });
    customerStore.fetchCustomers();
  },
  { immediate: true }
);
</script>
